import { EnrichmentPriceItem } from "@app/shared/models/enrichment-detail.model";
import {
  isPublishableStatus,
  isRejectableStatus,
  isStageableStatus,
} from "@app/shared/models/enrichment-list.model";
import { isPublishablePriceType } from "../services/publish/utils";
import { priceStatusChip } from "./price-entry.util";

export type LifecycleGate =
  { readonly ok: true } | { readonly ok: false; readonly reason: string };

/**
 * What can be done to ONE price now. The hero gates and the publish drawer
 * read the same answer. Permission is not in here: the shell ANDs it in.
 */
export interface PriceLifecycleActions {
  readonly publish: LifecycleGate;
  readonly reject: LifecycleGate;
  readonly stageLabel: string | null;
}

const ok: LifecycleGate = { ok: true };
const no = (reason: string): LifecycleGate => ({ ok: false, reason });

function publishGate(
  status: string | undefined,
  priceType: unknown,
): LifecycleGate {
  if (!isPublishablePriceType(priceType)) return no("Unsupported price type");
  if (isPublishableStatus(status)) return ok;
  if (isRejectableStatus(status)) return no("Already live");
  if ((status ?? "").toLowerCase() === "rejected")
    return no("Save to re-stage");
  return no("Save first");
}

export function lifecycleActions(
  price: Pick<EnrichmentPriceItem, "lifecycleStatus" | "priceType">,
): PriceLifecycleActions {
  const status = price.lifecycleStatus?.trim();
  return {
    publish: publishGate(status, price.priceType),
    reject: isRejectableStatus(status) ? ok : no("Not on sale"),
    stageLabel: isStageableStatus(status)
      ? (priceStatusChip(status)?.label ?? null)
      : null,
  };
}

type LifecyclePrice = Pick<
  EnrichmentPriceItem,
  "id" | "lifecycleStatus" | "priceType"
>;

export function nextToPublish<T extends { readonly id: string }>(
  order: readonly T[],
  prices: readonly LifecyclePrice[],
  currentId: string | null,
): T | null {
  const publishable = (id: string): boolean => {
    const price = prices.find((p) => p.id === id);
    return !!price && lifecycleActions(price).publish.ok;
  };
  const start = order.findIndex((entry) => entry.id === currentId);
  const count = order.length;
  for (let step = 1; step <= count; step++) {
    const index = (start + step) % count;
    if (index === start) continue;
    if (publishable(order[index].id)) return order[index];
  }
  return null;
}
